import { useCallback, useMemo, useState } from 'react'

type FilterTabCounts<TFilter extends string> = Partial<Record<TFilter, number>>

export function useFilterTabCounts<TFilter extends string>({
  filterKeys,
  forceFetchAllCounts = false,
  minimumIncomingCountKeys = 2,
}: {
  filterKeys: readonly TFilter[]
  forceFetchAllCounts?: boolean
  minimumIncomingCountKeys?: number
}) {
  const [totalCounts, setTotalCounts] = useState<FilterTabCounts<TFilter>>({})

  const mergeTotalCounts = useCallback(
    (incomingCounts?: FilterTabCounts<TFilter>) => {
      if (!incomingCounts) {
        return
      }

      if (Object.keys(incomingCounts).length < minimumIncomingCountKeys) {
        return
      }

      setTotalCounts((currentCounts) => ({ ...currentCounts, ...incomingCounts }))
    },
    [minimumIncomingCountKeys]
  )

  const clearTotalCounts = useCallback(() => {
    setTotalCounts({})
  }, [])

  const hasAllTabCounts = useMemo(() => {
    for (const filterKey of filterKeys) {
      if (totalCounts[filterKey] === undefined) {
        return false
      }
    }

    return true
  }, [filterKeys, totalCounts])

  const fetchAllCounts = forceFetchAllCounts || !hasAllTabCounts

  return {
    totalCounts,
    fetchAllCounts,
    mergeTotalCounts,
    clearTotalCounts,
  }
}
